import { template as _$template } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
import { use as _$use } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<p class=world-viewer__message>WebGPU is not available in this browser/context (<code>navigator.gpu</code> is undefined). The 2D map still works — this port targets three.js <code>WebGPURenderer</code> only, no WebGL fallback (AD-005).`),
  _tmpl$2 = /*#__PURE__*/_$template(`<p class=world-viewer__message>Initializing WebGPU…`),
  _tmpl$3 = /*#__PURE__*/_$template(`<p class="world-viewer__message world-viewer__message--error">WebGPU failed to initialize: `),
  _tmpl$4 = /*#__PURE__*/_$template(`<div class=world-viewer__overlay>`),
  _tmpl$5 = /*#__PURE__*/_$template(`<p class=world-viewer__hint> scenery draw calls (one per distinct object).`),
  _tmpl$6 = /*#__PURE__*/_$template(`<div class="world-viewer__pane world-viewer__pane--three"><div class=world-viewer__canvas-host>`);
import { createEffect, createSignal, onCleanup, Show } from "solid-js";
import { hasWebGpu } from "../three/webgpu-support.ts";
import { createWorldScene } from "./world-scene.ts";
/**
 * The World viewer's 3D pane: the region's resolved placements as instanced
 * meshes over the flat ground plane, orbit camera, same no-WebGPU
 * degradation as `ModelViewer.tsx`/`/fly` (AD-005). Unlike `ModelViewer.tsx`
 * (which tears down and rebuilds its whole scene per selection — cheap
 * there, one mesh at a time), this pane creates **one** WebGPU scene/GPU
 * device for its entire mount lifetime and swaps region contents through
 * `WorldScene.setRegion()` instead: rebuilding the renderer on every region
 * click used to spin up a fresh `GPUDevice` per click while the *previous*
 * device's teardown (`renderer.dispose()`, chained off `pendingInit` in
 * `world-scene.ts`) ran async and unawaited — under fast region switching
 * (a user browsing the World tab region by region) devices piled up faster
 * than the driver released them, growing renderer/GPU-process RSS without
 * bound until the browser crashed (user report 2026-08-19, reproduced on
 * real WebGPU: ~250MB->500MB+ and climbing over ~9 minutes of switching,
 * flat when the scene/device is reused instead). Re-frames on
 * `selectedPlacement` once ready.
 */
export function WorldThreePane(props) {
  const [status, setStatus] = createSignal(hasWebGpu() ? "initializing" : "unavailable");
  const [errorMessage, setErrorMessage] = createSignal(undefined);
  const [drawCalls, setDrawCalls] = createSignal(0);
  let containerRef;
  let scene;
  onCleanup(() => {
    scene?.dispose();
    scene = undefined;
  });

  // Runs once on mount (tracks no reactive signal) — the scene/GPU device outlives every region switch.
  createEffect(() => {
    if (!hasWebGpu() || !containerRef) {
      setStatus(hasWebGpu() ? "initializing" : "unavailable");
      return;
    }
    setStatus("initializing");
    setErrorMessage(undefined);
    scene = createWorldScene(containerRef, {
      onStatus: (next, message) => {
        setStatus(next);
        setErrorMessage(message);
      }
    });
  });

  // Push the loaded region into the (persistent) 3D scene whenever it changes, once the scene is
  // ready (mirrors ModelViewer.tsx's LOD-push effect) — `setRegion` disposes the previous region's
  // meshes within the same renderer, no device teardown/recreate involved.
  createEffect(() => {
    const region = props.region;
    const activeScene = scene;
    if (status() !== "ready" || !region || !activeScene) return;
    setDrawCalls(0);
    void activeScene
    // `source.wldPath` is the region's identity in the bake, and what the
    // scene keys its retained-region cache on (`world-scene.ts`).
    .setRegion({
      key: region.source.wldPath,
      bounds: region.bounds,
      placements: region.placements
    }).then(() => setDrawCalls(activeScene.drawCallCount()));
  });

  // Keep the focused 3D placement in sync with the map selection.
  createEffect(() => {
    const placement = props.selectedPlacement;
    if (status() !== "ready" || !scene || !placement) return;
    scene.focusPlacement(placement);
  });
  return (() => {
    var _el$ = _tmpl$6(),
      _el$2 = _el$.firstChild;
    var _ref$ = containerRef;
    typeof _ref$ === "function" ? _$use(_ref$, _el$2) : containerRef = _el$2;
    _$insert(_el$, _$createComponent(Show, {
      get when() {
        return status() !== "ready";
      },
      get children() {
        var _el$3 = _tmpl$4();
        _$insert(_el$3, _$createComponent(Show, {
          get when() {
            return status() === "unavailable";
          },
          get children() {
            return _tmpl$();
          }
        }), null);
        _$insert(_el$3, _$createComponent(Show, {
          get when() {
            return status() === "initializing";
          },
          get children() {
            return _tmpl$2();
          }
        }), null);
        _$insert(_el$3, _$createComponent(Show, {
          get when() {
            return status() === "error";
          },
          get children() {
            var _el$6 = _tmpl$3(),
              _el$7 = _el$6.firstChild;
            _$insert(_el$6, errorMessage, null);
            return _el$6;
          }
        }), null);
        return _el$3;
      }
    }), null);
    _$insert(_el$, _$createComponent(Show, {
      get when() {
        return status() === "ready";
      },
      get children() {
        var _el$8 = _tmpl$5(),
          _el$9 = _el$8.firstChild;
        _$insert(_el$8, drawCalls, _el$9);
        return _el$8;
      }
    }), null);
    return _el$;
  })();
}